import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { RouteReuseStrategy, provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { IonicRouteStrategy } from '@ionic/angular';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { provideServiceWorker } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';

import {
  MsalBroadcastService,
  MsalGuard,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';

import { API_BASE_URL, ODATA_BASE_URL } from '@postal-bridge/shared/api';

import { appRoutes } from './app.routes';
import { environment } from 'environments/environment';
import { loadRuntimeConfig } from './core/config/runtime-config';
import { MSALGuardConfigFactory, MSALInstanceFactory, MSALInterceptorConfigFactory } from './core/auth/msal-config';
import {
  apiLoggingInterceptorFn,
  authInterceptorFn,
  clientVersionInterceptorFn,
  debugInterceptorFn,
  requestIdInterceptorFn,
} from './core/http/http-interceptors';
import { ThemeService } from './core/theme/theme.service';
import { ApiLogStoreService } from './core/logging/api-log-store.service';

function msalInitializer(msal: MsalService) {
  return () => msal.instance.initialize();
}

function themeInitializer(theme: ThemeService) {
  return () => theme.init();
}

function apiLogInitializer(store: ApiLogStoreService) {
  return () => store.init();
}

export const appConfig: ApplicationConfig = {
  providers: [
    // These apps were written around Zone-driven async view updates. Angular
    // 21 defaults to zoneless change detection, which left HTTP-backed screens
    // permanently showing their loading skeleton on iOS even after 200s.
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideIonicAngular(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production && Capacitor.getPlatform() === 'web',
      registrationStrategy: 'registerWhenStable:30000',
    }),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },

    provideRouter(appRoutes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),

    // Runtime-config (assets/config.<env>.json) override
    { provide: APP_INITIALIZER, useFactory: loadRuntimeConfig(), multi: true },

    // Theme (light/dark/system)
    { provide: APP_INITIALIZER, useFactory: themeInitializer, deps: [ThemeService], multi: true },

    // API logging store (prune/retention)
    { provide: APP_INITIALIZER, useFactory: apiLogInitializer, deps: [ApiLogStoreService], multi: true },

    // MSAL
    importProvidersFrom(MsalModule),
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory, deps: [MSAL_INSTANCE] },
    MsalBroadcastService,
    MsalGuard,
    MsalService,
    { provide: APP_INITIALIZER, useFactory: msalInitializer, deps: [MsalService], multi: true },

    // Http
    provideHttpClient(withInterceptors([
      requestIdInterceptorFn,
      clientVersionInterceptorFn,
      apiLoggingInterceptorFn,
      debugInterceptorFn,
      authInterceptorFn,
    ])),

    // Shared base-url tokens
    {
      provide: API_BASE_URL,
      useFactory: () => String(environment.apiConfig.apiUrl).replace(/\/+$/, ''),
    },
    {
      provide: ODATA_BASE_URL,
      useFactory: () => String(environment.apiConfig.odataUrl).replace(/\/+$/, ''),
    },
  ],
};
